import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * A small (i) icon that explains a label, metric or control. Hover or focus it
 * on desktop; tap it on touch screens. Put it right after the thing it explains:
 *
 *   <span>Show rate <InfoTip>Showed calls divided by confirmed bookings.</InfoTip></span>
 *
 * `article` adds a "Learn more" link into the Help Center.
 */
export function InfoTip({
  children, side = "top", article, className,
}: {
  children: ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  article?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        {/* A span, not a <button>: a button inside a <label> is treated as the label's
            control, so clicking the label text would trigger this instead of the field. */}
        <span
          role="button"
          tabIndex={0}
          aria-label="More info"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }
          }}
          className={cn(
            // The invisible ::after widens the tap target to ~32px on phones without moving the layout.
            "relative inline-flex size-4 shrink-0 cursor-help items-center justify-center rounded-full align-middle text-muted-foreground/70 transition-colors after:absolute after:-inset-2 after:content-[''] hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className,
          )}
        >
          <Info className="size-3.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent side={side} className="max-w-xs text-xs leading-relaxed">
        <div>{children}</div>
        {article && (
          <Link to={`/help/${article}`} className="mt-1 inline-block font-medium text-primary hover:underline">Learn more</Link>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Adds a tooltip to any existing element (an icon button, a badge, a heading):
 *
 *   <Hint tip="Copies the link"><Button size="icon">…</Button></Hint>
 *
 * The child must accept a ref and hover events. A disabled button does not fire
 * hover events, so wrap it in a span instead.
 */
export function Hint({
  tip, children, side = "top",
}: {
  tip: ReactNode;
  children: ReactNode;
  side?: "top" | "right" | "bottom" | "left";
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} className="max-w-xs text-xs leading-relaxed">{tip}</TooltipContent>
    </Tooltip>
  );
}
